import FormWithProvider from 'components/forms/form/Form';
import LoadingPlaceholder from 'components/loader/LoadingPlaceholder';
import useFetchSeasonalRuleDetail from 'components/domain/property/seasonality/useFetchSeasonalRuleDetail';
import { SeasonalRuleDetail } from 'models/seasonality/SeasonalRule';
import { Alert, Modal, ModalBody } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
  getFormDefaultValues,
  seasonalRuleFormSchema,
} from './SeasonalRuleAddEditModal.schema';
import SeasonalRuleAddEditModalBody from './SeasonalRuleAddEditModalBody';
import SeasonalRuleAddEditModalFooter from './SeasonalRuleAddEditModalFooter';
import { SeasonalRuleFormValues } from './SeasonalRuleAddEditModal.types';

interface Props {
  onSubmit: (values: SeasonalRuleFormValues) => Promise<SeasonalRuleDetail>;
  closeModal: () => void;
  seasonalRuleUid?: string;
  currentProperty?: { uid: string; name: string };
}

const SeasonalRuleAddEditModal = ({
  onSubmit,
  closeModal,
  seasonalRuleUid,
  currentProperty,
}: Props) => {
  const { t } = useTranslation();
  const isEditMode = !!seasonalRuleUid && seasonalRuleUid !== 'add';

  const {
    data: rule,
    isLoading,
    isError,
  } = useFetchSeasonalRuleDetail({
    seasonalRuleUid: isEditMode ? seasonalRuleUid : undefined,
  });

  const title = t(
    isEditMode
      ? 'pageProperty.seasonality.modal.title.edit'
      : 'pageProperty.seasonality.modal.title.create',
  );

  if (isEditMode && isLoading) {
    return (
      <>
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <ModalBody>
          <LoadingPlaceholder linesCount={6} />
        </ModalBody>
      </>
    );
  }

  if (isEditMode && isError) {
    return (
      <>
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <ModalBody>
          <Alert bsStyle="danger">
            {t('pageProperty.seasonality.modal.fetchError')}
          </Alert>
        </ModalBody>
      </>
    );
  }

  if (isEditMode && !rule) {
    return null;
  }

  return (
    <FormWithProvider
      defaultValues={getFormDefaultValues(rule, currentProperty)}
      onSubmit={onSubmit}
      resolver={seasonalRuleFormSchema()}
      noValidate
    >
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <SeasonalRuleAddEditModalBody />
      <SeasonalRuleAddEditModalFooter closeModal={closeModal} />
    </FormWithProvider>
  );
};

SeasonalRuleAddEditModal.defaultProps = {
  seasonalRuleUid: undefined,
  currentProperty: undefined,
};

export default SeasonalRuleAddEditModal;
